import type { ComponentType, ReactNode } from 'react';
import {
  CheckIcon,
  ChevronRightIcon,
  CompassIcon,
  FileTextIcon,
  ListChecksIcon,
  LoaderCircleIcon,
  PlugIcon,
} from 'lucide-react';
import { formatCopy } from '@/i18n/ns';
import type { WelcomeCopy } from '@/i18n/ns/welcome';
import type { ScopedConnectionItem } from '@/workspace/connection-api';
import {
  firstTasks,
  onboardingTools,
  starterTasksFor,
  type StarterTaskId,
} from './onboarding-tools';

const STARTER_ICONS: Record<StarterTaskId, ComponentType<{ 'aria-hidden'?: boolean }>> = {
  research: CompassIcon,
  file: FileTextIcon,
  'first-task': ListChecksIcon,
};

function TaskCard({
  icon,
  title,
  busy,
  pending,
  onChoose,
}: {
  icon: ReactNode;
  title: string;
  busy: boolean;
  pending: boolean;
  onChoose: () => void;
}) {
  return (
    <button
      className="onboarding-task"
      disabled={busy}
      aria-busy={pending || undefined}
      onClick={onChoose}
    >
      <span className="onboarding-task-icon">{icon}</span>
      <span className="onboarding-task-title">{title}</span>
      {pending ? (
        <LoaderCircleIcon
          className="onboarding-task-end animate-spin motion-reduce:animate-none"
          aria-hidden="true"
        />
      ) : (
        <ChevronRightIcon className="onboarding-task-end" aria-hidden="true" />
      )}
    </button>
  );
}

export function FirstStep({
  connected,
  busy,
  pending,
  copy,
  onChoose,
}: {
  connected: ScopedConnectionItem[];
  busy: boolean;
  /** The task whose conversation is starting, if any. */
  pending: string | null;
  copy: WelcomeCopy;
  onChoose: (prompt: string, id: string) => void;
}) {
  return (
    <div className="onboarding-first-work">
      {connected.length > 0 && (
        <p className="onboarding-connected">
          <CheckIcon aria-hidden="true" />
          {connected.length === 1
            ? copy.connectedOne
            : formatCopy(copy.connectedMany, { count: connected.length })}
        </p>
      )}
      {firstTasks(connected, copy)
        .slice(0, 2)
        .map((task) => (
          <TaskCard
            key={task.id}
            icon={
              Object.hasOwn(onboardingTools, task.id) ? (
                <img src={'/connector-logos/' + task.id + '.svg'} alt="" />
              ) : (
                <PlugIcon aria-hidden="true" />
              )
            }
            title={task.title}
            busy={busy}
            pending={pending === task.id}
            onChoose={() => onChoose(task.prompt, task.id)}
          />
        ))}
      {starterTasksFor(connected.length).map((task) => {
        const Icon = STARTER_ICONS[task.id];
        return (
          <TaskCard
            key={task.id}
            icon={<Icon aria-hidden />}
            title={copy[task.title]}
            busy={busy}
            pending={pending === task.id}
            onChoose={() => onChoose(copy[task.prompt], task.id)}
          />
        );
      })}
    </div>
  );
}
